import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { generatedFileResponseSchema } from "./imports.js";
import { paisa } from "./money.js";
import { businessPhoneSchema } from "./settings.js";
/** Customers, addresses and the credit ledger (docs/specs/customers.md, CU-01 … CU-05). */
// Reference data ---------------------------------------------------------------------------------------------------
export const geoAreaSchema = z.object({
    id: z.int(),
    parentId: z.int().nullable(),
    name: z.string(),
    nameBn: z.string(),
});
export const geoAreaListSchema = z.object({ data: z.array(geoAreaSchema) });
const geoId = z.coerce.number().int().min(1).max(32_767);
// Customers --------------------------------------------------------------------------------------------------------
const label = z.string().trim().min(1).max(50);
const labels = z
    .array(label)
    .max(20)
    .refine((items) => new Set(items.map((i) => i.toLowerCase())).size === items.length, "Each once");
const version = z.int().min(1);
export const customerAddressSchema = z.object({
    id: z.uuid(),
    label: z.string(),
    recipientName: z.string(),
    phone: z.string().nullable(),
    divisionId: z.int().nullable(),
    districtId: z.int().nullable(),
    upazilaId: z.int().nullable(),
    /** `Upazila, District, Division` in English; the Admin builds the Bengali one from the reference data. */
    area: z.string(),
    line: z.string(),
    isDefault: z.boolean(),
});
export const customerSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    /** E.164. */
    phone: z.string(),
    altPhone: z.string().nullable(),
    email: z.string(),
    tags: z.array(z.string()),
    groups: z.array(z.string()),
    note: z.string(),
    /** 0 = no credit sales. */
    creditLimitPaisa: paisa(),
    /** What the customer owes (negative: paid in advance). */
    balancePaisa: paisa(),
    lastRemindedAt: z.iso.datetime().nullable(),
    version: z.int(),
    createdAt: z.iso.datetime(),
});
export const customerDetailSchema = customerSchema.extend({ addresses: z.array(customerAddressSchema) });
export const customerListQuerySchema = z.object({
    q: z.string().trim().max(100).optional(),
    tag: z.string().trim().max(50).optional(),
    group: z.string().trim().max(50).optional(),
    /** Only customers who owe something. */
    due: z.enum(["true"]).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const customerListSchema = z.object({
    data: z.array(customerSchema),
    nextCursor: z.uuid().nullable(),
});
export const customerLookupQuerySchema = z.object({ phone: businessPhoneSchema });
export const addressInputSchema = z
    .object({
    label: z.string().trim().max(50).default(""),
    recipientName: z.string().trim().max(200).default(""),
    phone: businessPhoneSchema.nullable().default(null),
    divisionId: z.int().min(1).nullable().default(null),
    districtId: z.int().min(1).nullable().default(null),
    upazilaId: z.int().min(1).nullable().default(null),
    line: z.string().trim().min(1).max(300),
    isDefault: z.boolean().default(false),
})
    .strict();
const profileFields = {
    name: z.string().trim().min(1).max(200),
    phone: businessPhoneSchema,
    altPhone: businessPhoneSchema.nullable().optional(),
    email: z.union([z.literal(""), z.email().max(200)]).optional(),
    tags: labels.optional(),
    groups: labels.optional(),
    note: z.string().trim().max(1000).optional(),
    creditLimitPaisa: paisa().min(0).optional(),
};
/** `POST /v1/customers`; the opening due (CU-02) is entered once, here or by import. */
export const createCustomerRequestSchema = z
    .object({ ...profileFields, openingDuePaisa: paisa().optional(), address: addressInputSchema.optional() })
    .strict();
export const updateCustomerRequestSchema = z
    .object({
    name: profileFields.name.optional(),
    phone: profileFields.phone.optional(),
    altPhone: profileFields.altPhone,
    email: profileFields.email,
    tags: profileFields.tags,
    groups: profileFields.groups,
    note: profileFields.note,
    creditLimitPaisa: profileFields.creditLimitPaisa,
    version,
})
    .strict();
export const addressParamsSchema = z.object({ id: z.uuid(), addressId: z.uuid() });
export const mergeCustomerRequestSchema = z.object({ intoCustomerId: z.uuid() }).strict();
// Credit ledger ----------------------------------------------------------------------------------------------------
export const CREDIT_ENTRY_TYPES = [
    "opening",
    "credit_sale",
    "sale_return",
    "payment",
    "adjustment",
    "reversal",
    "merge_in",
    "merge_out",
];
export const creditEntryTypeSchema = z.enum(CREDIT_ENTRY_TYPES);
export const CUSTOMER_PAYMENT_METHODS = [
    "cash",
    "card",
    "bkash",
    "nagad",
    "rocket",
    "bank_transfer",
    "other",
];
export const customerPaymentMethodSchema = z.enum(CUSTOMER_PAYMENT_METHODS);
export const creditEntrySchema = z.object({
    id: z.uuid(),
    customerId: z.uuid(),
    type: creditEntryTypeSchema,
    /** Positive: the customer owes more. */
    amountPaisa: paisa(),
    balanceAfterPaisa: paisa(),
    method: customerPaymentMethodSchema.nullable(),
    reference: z.string(),
    locationId: z.uuid().nullable(),
    sourceType: z.string().nullable(),
    sourceId: z.uuid().nullable(),
    note: z.string(),
    reversalOfId: z.uuid().nullable(),
    /** Set on an entry that has been reversed. */
    reversedById: z.uuid().nullable(),
    /** A receipt's payment account (A-01, B-54); null otherwise. */
    accountId: z.uuid().nullable(),
    occurredAt: z.iso.datetime(),
    createdBy: z.uuid().nullable(),
});
export const creditEntryListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const creditEntryListSchema = z.object({
    data: z.array(creditEntrySchema),
    nextCursor: z.uuid().nullable(),
});
/** `POST /v1/customers/{id}/payments`: money received against dues (partial is fine; more leaves an advance). */
export const recordCustomerPaymentRequestSchema = z
    .object({
    amountPaisa: paisa().positive(),
    method: customerPaymentMethodSchema,
    reference: z.string().trim().max(100).default(""),
    locationId: z.uuid().optional(),
    note: z.string().trim().max(500).default(""),
    /** Received into this account: a payment is recorded there too (B-54). */
    accountId: z.uuid().nullable().default(null),
})
    .strict();
/** `POST /v1/customers/{id}/credit-adjustments`: a signed correction with a reason. */
export const creditAdjustmentRequestSchema = z
    .object({
    amountPaisa: paisa().refine((v) => v !== 0, "Cannot be zero"),
    note: z.string().trim().min(1).max(500),
})
    .strict();
export const reverseCreditEntryRequestSchema = z.object({ note: z.string().trim().min(1).max(500) }).strict();
/** `POST /v1/customers/{id}/statement`: a period (days in the tenant's zone); default the last 90 days. */
export const customerStatementRequestSchema = z
    .object({ from: z.iso.date().optional(), to: z.iso.date().optional() })
    .strict();
export const dueReminderResponseSchema = z.object({ messageId: z.uuid() });
export const bulkDueRemindersRequestSchema = z.object({ minDuePaisa: paisa().min(1).default(1) }).strict();
export const bulkDueRemindersResponseSchema = z.object({ queued: z.literal(true) });
// Events -------------------------------------------------------------------------------------------------------------
export const customerEventSchema = customerSchema.omit({ balancePaisa: true, lastRemindedAt: true });
export const customerMergedEventSchema = z.object({
    sourceId: z.uuid(),
    targetId: z.uuid(),
    movedBalancePaisa: paisa(),
});
export const creditPostedEventSchema = creditEntrySchema.omit({ reversedById: true }).extend({
    /** On a reversal: the type of the entry it reverses (accounting tells a returned receipt from an adjustment). */
    reversalOfType: creditEntryTypeSchema.nullable().optional(),
});
// Endpoints ----------------------------------------------------------------------------------------------------------
const tag = { tag: "customers" };
const view = { permission: "customers.view" };
const manage = { permission: "customers.manage" };
const credit = { permission: "customers.credit" };
const geo = (name, path, query) => defineEndpoint({
    ...tag,
    name,
    method: "GET",
    path,
    access: { signedIn: true },
    summary: "Bangladesh administrative areas (reference data)",
    query,
    status: 200,
    response: geoAreaListSchema,
});
export const listDivisions = geo("listDivisions", "/v1/geo/divisions", z.object({}));
export const listDistricts = geo("listDistricts", "/v1/geo/districts", z.object({ divisionId: geoId.optional() }));
export const listUpazilas = geo("listUpazilas", "/v1/geo/upazilas", z.object({ districtId: geoId }));
export const listCustomers = defineEndpoint({
    ...tag,
    name: "listCustomers",
    method: "GET",
    path: "/v1/customers",
    access: view,
    summary: "Customers by name; search name or phone, filter by tag, group or dues",
    query: customerListQuerySchema,
    status: 200,
    response: customerListSchema,
});
export const lookupCustomer = defineEndpoint({
    ...tag,
    name: "lookupCustomer",
    method: "GET",
    path: "/v1/customers/lookup",
    access: view,
    summary: "The customer with this phone (or alt phone), as typed",
    query: customerLookupQuerySchema,
    status: 200,
    response: customerDetailSchema,
});
export const createCustomer = defineEndpoint({
    ...tag,
    name: "createCustomer",
    method: "POST",
    path: "/v1/customers",
    access: manage,
    summary: "Add a customer",
    body: createCustomerRequestSchema,
    status: 201,
    response: customerDetailSchema,
    errors: ["CONFLICT"],
});
export const getCustomer = defineEndpoint({
    ...tag,
    name: "getCustomer",
    method: "GET",
    path: "/v1/customers/{id}",
    access: view,
    summary: "One customer with addresses and balance",
    params: idParamsSchema,
    status: 200,
    response: customerDetailSchema,
});
export const updateCustomer = defineEndpoint({
    ...tag,
    name: "updateCustomer",
    method: "PATCH",
    path: "/v1/customers/{id}",
    access: manage,
    summary: "Change the profile (credit limit needs customers.credit)",
    params: idParamsSchema,
    body: updateCustomerRequestSchema,
    status: 200,
    response: customerDetailSchema,
    errors: ["CONFLICT"],
});
export const addCustomerAddress = defineEndpoint({
    ...tag,
    name: "addCustomerAddress",
    method: "POST",
    path: "/v1/customers/{id}/addresses",
    access: manage,
    summary: "Add an address",
    params: idParamsSchema,
    body: addressInputSchema,
    status: 201,
    response: customerDetailSchema,
});
export const updateCustomerAddress = defineEndpoint({
    ...tag,
    name: "updateCustomerAddress",
    method: "PATCH",
    path: "/v1/customers/{id}/addresses/{addressId}",
    access: manage,
    summary: "Replace an address",
    params: addressParamsSchema,
    body: addressInputSchema,
    status: 200,
    response: customerDetailSchema,
});
export const removeCustomerAddress = defineEndpoint({
    ...tag,
    name: "removeCustomerAddress",
    method: "DELETE",
    path: "/v1/customers/{id}/addresses/{addressId}",
    access: manage,
    summary: "Remove an address",
    params: addressParamsSchema,
    status: 200,
    response: customerDetailSchema,
});
export const listCreditEntries = defineEndpoint({
    ...tag,
    name: "listCreditEntries",
    method: "GET",
    path: "/v1/customers/{id}/credit-entries",
    access: view,
    summary: "The dues ledger, newest first, with the balance after each entry",
    params: idParamsSchema,
    query: creditEntryListQuerySchema,
    status: 200,
    response: creditEntryListSchema,
});
export const recordCustomerPayment = defineEndpoint({
    ...tag,
    name: "recordCustomerPayment",
    method: "POST",
    path: "/v1/customers/{id}/payments",
    access: credit,
    summary: "Money received against dues",
    params: idParamsSchema,
    body: recordCustomerPaymentRequestSchema,
    status: 201,
    response: creditEntrySchema,
    idempotent: true,
});
export const adjustCustomerCredit = defineEndpoint({
    ...tag,
    name: "adjustCustomerCredit",
    method: "POST",
    path: "/v1/customers/{id}/credit-adjustments",
    access: credit,
    summary: "A signed correction of the dues, with a reason",
    params: idParamsSchema,
    body: creditAdjustmentRequestSchema,
    status: 201,
    response: creditEntrySchema,
    idempotent: true,
});
export const reverseCreditEntry = defineEndpoint({
    ...tag,
    name: "reverseCreditEntry",
    method: "POST",
    path: "/v1/customer-credit-entries/{id}/reverse",
    access: credit,
    summary: "Undo a payment or adjustment entered by mistake",
    params: idParamsSchema,
    body: reverseCreditEntryRequestSchema,
    status: 201,
    response: creditEntrySchema,
    errors: ["ALREADY_REVERSED"],
    idempotent: true,
});
export const customerStatement = defineEndpoint({
    ...tag,
    name: "customerStatement",
    method: "POST",
    path: "/v1/customers/{id}/statement",
    access: view,
    summary: "The dues statement for a period as a PDF (signed URL)",
    params: idParamsSchema,
    body: customerStatementRequestSchema,
    status: 201,
    response: generatedFileResponseSchema,
});
export const sendDueReminder = defineEndpoint({
    ...tag,
    name: "sendDueReminder",
    method: "POST",
    path: "/v1/customers/{id}/due-reminder",
    access: credit,
    summary: "SMS the customer what they owe (once a day at most)",
    params: idParamsSchema,
    status: 201,
    response: dueReminderResponseSchema,
    errors: ["NOTHING_DUE", "RATE_LIMITED"],
});
export const sendDueReminders = defineEndpoint({
    ...tag,
    name: "sendDueReminders",
    method: "POST",
    path: "/v1/customers/due-reminders",
    access: credit,
    summary: "SMS every customer who owes at least the minimum (a job)",
    body: bulkDueRemindersRequestSchema,
    status: 202,
    response: bulkDueRemindersResponseSchema,
});
export const mergeCustomer = defineEndpoint({
    ...tag,
    name: "mergeCustomer",
    method: "POST",
    path: "/v1/customers/{id}/merge",
    access: { permission: "customers.merge" },
    summary: "Merge this customer into another: addresses, dues and orders move",
    params: idParamsSchema,
    body: mergeCustomerRequestSchema,
    status: 200,
    response: customerDetailSchema,
});
//# sourceMappingURL=customers.js.map